import { useNavigate } from "@tanstack/react-router";
import React from "react";
import type { HTMLAttributes } from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/cn";

interface DiscoveryStepProgressProps {
  totalSteps: number;
  currentIndex: number;
}

const DiscoveryStepProgress = ({
  totalSteps,
  currentIndex,
}: DiscoveryStepProgressProps) => {
  const navigate = useNavigate();

  const navigateToStep = (stepNumber: number) => {
    navigate({ to: `/discovery/steps/${stepNumber}` });
  };

  return (
    <div className="flex items-center gap-x-2">
      {Array.from({ length: totalSteps }, (_, index) => (
        <div
          key={index}
          onClick={
            index < currentIndex + 1
              ? () => navigateToStep(index + 1)
              : undefined
          }
          className={cn(
            "h-2 flex-1 rounded",
            index < currentIndex + 1 ? "bg-brand-400" : "bg-brand-100",
            {
              "cursor-pointer transition hover:bg-brand-500":
                index < currentIndex + 1,
            },
          )}
        ></div>
      ))}
    </div>
  );
};

interface DiscoveryStepCardProps extends HTMLAttributes<HTMLDivElement> {
  error?: unknown;
  children?: React.ReactNode;
}

const DiscoveryStepCard = ({
  className,
  error,
  children,
  ...rest
}: DiscoveryStepCardProps) => {
  return (
    <div
      className={cn(
        "mt-6 rounded-2xl border border-gray-100 bg-gray-25 p-8",
        {
          "border-red-700": error,
        },
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

interface DiscoveryStepControlProps {
  next?: () => void;
  prev?: () => void;
  type?: "button" | "submit";
  disabled?: boolean;
  loading?: boolean;
}

const DiscoveryStepControl = (props: DiscoveryStepControlProps) => {
  return (
    <div
      className={cn("mt-6 flex justify-end", {
        "justify-between": props.prev,
      })}
    >
      {props.prev && (
        <Button type="button" onClick={props.prev} variant="ghost">
          Back
        </Button>
      )}
      <Button
        type={props.type}
        onClick={props.next}
        disabled={props.disabled}
        loading={props.loading}
        variant="primary"
      >
        Next
      </Button>
    </div>
  );
};

interface DiscoveryStepTitleProps {
  title?: string;
}

const DiscoveryStepTitle = ({ title }: DiscoveryStepTitleProps) => {
  return (
    <h1 className="text-text-primary mt-12 px-4 text-center font-heading text-3xl font-medium tracking-tight">
      {title}
    </h1>
  );
};

const DiscoveryStepSubTitle = ({ title }: DiscoveryStepTitleProps) => {
  return (
    <h3 className="text-text-secondary mt-4 px-8 text-center text-lg">
      {title}
    </h3>
  );
};

/** Layout pieces of one coach-discovery step: Card, Title, SubTitle, Control and Progress. */
export const DiscoveryStep = {
  Card: DiscoveryStepCard,
  Title: DiscoveryStepTitle,
  SubTitle: DiscoveryStepSubTitle,
  Control: DiscoveryStepControl,
  Progress: DiscoveryStepProgress,
};
